import { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { apiPost, apiPut } from '../../services/genesisApi/httpsMethods';
import { CustomButton } from '../../components/common/customButton';
import { ConfirmDialog } from '../../components/common/confirmDialog';
import { SimpleToolbar } from '../../components/layout/Toolbar';
import { DynamicForm } from '../../components/common/DynamicForm';
import { sourceFieldConfig } from '../../config/sourceFieldConfig';
import type { Source } from '../../types/deal';

interface CreateSourceProps {
  source?: Partial<Source>;
  isEdit?: boolean;
}

const emptySource: Partial<Source> = {
  source_name: '',
  source_type: '',
};

const CreateSource = ({ source: initialSource, isEdit }: CreateSourceProps) => {
  const [source, setSource] = useState<Partial<Source>>(initialSource ?? emptySource);
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();
  const sourceId = typeof source?.id === 'number' ? source.id : undefined;

  useEffect(() => {
    if (!initialSource) {
      setSource({ ...emptySource });
    }
  }, [initialSource]);

  const handleChange = (field: string, value: any) => {
    const cleanedValue = value === '' || value === undefined ? null : value;
    setSource((prev) => ({ ...prev, [field]: cleanedValue }));
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        source_name: (source.source_name ?? '').toString(),
        source_type: source.source_type ?? null,
      };

      if (initialSource && !sourceId) {
        console.error('Cannot update Source: missing source.id');
        return;
      }

      const response = initialSource
        ? await apiPut('source', sourceId as number, payload)
        : await apiPost('sources', payload);

      if (response && response.success) {
        navigate(initialSource ? `/sources/${sourceId}` : '/sources');
      } else {
        console.error('Source save failed:', response?.message || 'Unknown error');
      }
    } catch (error) {
      console.error('Error saving source:', error);
    }
  };

  return (
    <Box sx={{ position: 'relative', zIndex: 1 }}>
      <Box sx={{ position: 'sticky', zIndex: 10, marginBottom: '10px' }}>
        <SimpleToolbar>
          {initialSource && (
            <CustomButton
              handleButtonPress={() => navigate(`/sources/${source.id}`)}
              isButtonDisabled={false}
              solid={false}
              text="Cancel"
              data-position="left"
            />
          )}
          <CustomButton
            handleButtonPress={handleSubmit}
            isButtonDisabled={!source?.source_name || (Boolean(initialSource) && !sourceId)}
            text={initialSource ? 'Update Source' : 'Create Source'}
            data-position="left"
          />

          <ConfirmDialog
            open={dialogOpen}
            endpoint="source"
            id={sourceId as number}
            title="Delete Source"
            description="Are you sure you want to delete this source? This action cannot be undone."
            confirmText="Delete"
            cancelText="Cancel"
            onClose={() => setDialogOpen(false)}
            onSuccess={() => navigate('/sources')}
          />

          {isEdit && sourceId && (
            <CustomButton
              handleButtonPress={() => setDialogOpen(true)}
              isButtonDisabled={false}
              text="Delete Source"
              isDeleteButton
              data-position="right"
            />
          )}
        </SimpleToolbar>
      </Box>

      <Box sx={{ maxWidth: '95%', margin: '0 auto', padding: 4 }}>
        <DynamicForm
          config={sourceFieldConfig}
          values={source}
          onChange={handleChange}
          numColumns={1}
        />
      </Box>
    </Box>
  );
};

export default CreateSource;


